import { NGROK } from "../../../../APIs";
import { apiClient } from "../../../../utils/apiClient";
import {
  AddManualDeviceCredential,
  AddManualDevicePayload,
  Computer,
  DeviceCountParams,
  GetComputersParams,
  GetComputersParamsPerDevice,
  Tag,
  ValidateGatewayResponse,
} from "../../interfaces/EndpointInterfaces";
import { createGuacConnection, getToken, saveToken } from "./computerApi";

export interface NetworkScanOpenPort {
  hostname?: string | null;
  ipAddress: string;
  port: number;
  onboarded?: boolean;
  operatingSystem?: string | null;
  lastSeen?: string | null;
}

export interface NetworkScanResultItem {
  cidr: string;
  openPorts?: NetworkScanOpenPort[];
}

export interface NetworkScanResultResponse {
  status: string;
  scan_result_id?: number;
  computer_id?: number;
  tenant_name?: string;
  scanner?: {
    type?: string;
    version?: string;
  };
  total_cidrs?: number;
  total_open_ports?: number;
  created_at?: string;
  results?: NetworkScanResultItem[];
}

export interface NetworkScanStatusResponse {
  status: boolean;
  lastScanTime?: string;
  last_scan_time?: string;
}

export const getComputers = async ({
  tenantName,
  email,
  role,
  page,
  limit,
  searchMachine = "",
  userId,
}: GetComputersParams): Promise<any[]> => {


  if (!email || !role || !tenantName) return [];


  let url = `${NGROK}/api/${tenantName}/computers`;
  const params: Record<string, any> = {
    email,
    role,
    page: page + 1,
    limit,
  };

  if (searchMachine.length > 0) {
    params.searchparams = searchMachine;
  }


  const data = await apiClient<any[]>({
    method: "GET",
    url,
    params,
  });

  const sorted = data.sort((a, b) => (a.id > b.id ? 1 : -1));

  if (role === "TENANT_USER") {
    return sorted;
  }

  return sorted;
};

export const getComputersCount = async ({
  tenantName,
  email,
  role,
  searchMachine = "",
  userId,
}: DeviceCountParams): Promise<number> => {
  if (!email || !role || !tenantName) return 0;

  const url = `${NGROK}/api/${tenantName}/computers/countDetails`;

  const params: Record<string, any> = {
    email,
    role,
    ...(searchMachine.length > 0 && { searchparams: searchMachine }),
  };

  const data = await apiClient<number>({
    method: "GET",
    url,
    params,
  });

  return data;
};

export const connectComputer = async (
  computer: Computer,
  email: string,
  userRole: "admin" | "user",
  mode?: string,
  label?: string,
  userId?: string,
  setSessionTab?: any,
  userApprovedNotifications: any[] = [],

): Promise<void> => {
  const connectionData = await createGuacConnection(computer, mode, label, userRole, userApprovedNotifications);
  if (!connectionData) {
    return;
  }

  const inputData = `${connectionData.identifier}\0c\0postgresql`;
  const mqString = btoa(inputData);

  let response = await getToken(email, computer.id, userRole);
  let authToken = response?.authToken;

  if (!authToken && userId) {
    response = await saveToken(email, computer, userRole, userId);
    authToken = response?.authToken;
  }
  if (authToken) {
    const finalUrl = `https://${computer.guachost}/#/client/${mqString}?token=${authToken}`;


    window.open(finalUrl, "_blank");
    if (!mode) {
      const sessionKey = `JIT|${computer.id}|${email}`;
      setSessionTab(sessionKey);
    }
  } else {
  }
};

export const changeFriendlyName = async ({
  tenantName,
  deviceId,
  friendlyName
}: {
  tenantName: string;
  deviceId: string;
  friendlyName: string;
}) => {
  const url = `${NGROK}/api/${deviceId}/friendly-name`;

  const data = await apiClient({
    method: "PUT",
    url,
    params: { friendlyName, tenantName },
  });

  return data;
};

export const confirmNebulaChange = async ({
  deviceId,
  enableNebula,
}: {
  deviceId: string;
  enableNebula: boolean;
}) => {
  const url = `${NGROK}/api/computers/${deviceId}/nebula-status`;

  const data = await apiClient({
    method: "PUT",
    url,
    params: { enable: enableNebula },
  });

  return data;
};

export const updateGateway = async ({ deviceId, selectedSubnets }: { deviceId: string, selectedSubnets: string[] }) => {

  const url = `${NGROK}/api/set-gateway`;

  const data = await apiClient({
    method: "POST",
    url,
    data: { computerId: deviceId, gatewayIpAddresses: selectedSubnets },
  });
  return data;

}

export const validateIpAddress = async (tenantName: string, ipAddress: string, deviceName: string):
  Promise<ValidateGatewayResponse> => {

  console.log(tenantName, ipAddress, deviceName)
  const url = `${NGROK}/api/validate-device`;

  const data = await apiClient({
    method: "GET",
    url,
    params: { tenantName, ipAddress, deviceName },
  });
  return data;

}

export const performScanforDevice = async (deviceId: string) => {
  const url = `${NGROK}/api/perform-scan`;
  const data = await apiClient({
    method: "POST",
    url,
    data: {
      computerId: deviceId,
    }
  });
  return data;
}

export const performNetworkScan = async (
  deviceId: string,
  selectedSubnets: string[],
  timeout?: number,
) => {
  const url = `${NGROK}/api/perform-network-scan`;
  const payload: Record<string, any> = {
    computerId: deviceId,
    selectedSubnets,
  };

  if (typeof timeout === "number" && !Number.isNaN(timeout)) {
    payload.timeout = timeout;
  }

  const data = await apiClient({
    method: "POST",
    url,
    data: payload,
  });

  return data;
}

export const getNetworkScanResult = async (
  tenantName: string,
  computerId: string,
  signal?: AbortSignal,
): Promise<NetworkScanResultResponse | null> => {
  if (!tenantName || !computerId) {
    return null;
  }

  const url = `${NGROK}/api/network-scan-result`;
  const data = await apiClient<NetworkScanResultResponse>({
    method: "GET",
    url,
    params: {
      tenant_name: tenantName,
      computer_id: computerId,
    },
    signal,
  });

  return data;
}

export const getNetworkScanStatus = async (
  tenantName: string,
  computerId: string,
  signal?: AbortSignal,
): Promise<NetworkScanStatusResponse | null> => {
  if (!tenantName || !computerId) {
    return null;
  }

  const url = `${NGROK}/api/network-scan-status`;
  const data = await apiClient<NetworkScanStatusResponse>({
    method: "GET",
    url,
    params: {
      tenantName,
      computerId,
    },
    signal,
  });

  return data;
}

export const deleteDevice = async (deviceId: string) => {
  const url = `${NGROK}/api/computers/delete/${deviceId}`;

  const data = await apiClient({
    method: "DELETE",
    url,
  });

  return data;
};

export const getConfig = async (tenantName: string) => {
  if (!tenantName) return;
  const url = `${NGROK}/api/${tenantName}/generalsettings`;

  const data = await apiClient({
    method: "GET",
    url,
  });

  return data;
};

export const getRolesList = async (tenantName: string) => {
  if (!tenantName) return;
  const url = `${NGROK}/api/${tenantName}/users-role-details`;
  const data = await apiClient({
    method: "GET",
    url,
  });

  return data;
};

export const getProfileList = async (tenantName: string, computerId: string) => {
  const url = `${NGROK}/api/${tenantName}/computers/${computerId}/profiles`;
  const data = await apiClient({
    method: "GET",
    url,
  });

  return data;
};

export const updateComputer = async (tenantName: string, computerId: string, profileId: number) => {
  const url = `${NGROK}/api/${tenantName}/computers/${computerId}/profiles/${profileId}/update-computer`;
  const data = await apiClient({
    method: "PUT",
    url,
  });

  return data;
};

export const updateProfileByCategory = async (tenantName: string, computerId: string, profileId: number) => {
  const url = `${NGROK}/api/category/${tenantName}/computers/${computerId}/profiles/${profileId}`;
  const data = await apiClient({
    method: "PUT",
    url,
  });

  return data;
};

export const updateProfile = async (tenantName: string, computerId: string, profileId: number) => {
  const url = `${NGROK}/api/${tenantName}/computers/${computerId}/profiles/${profileId}`;
  const data = await apiClient({
    method: "PUT",
    url,
  });

  return data;
};

export const updateOperatingMode = async (computerId: string, mode: string, email: string) => {
  const url = `${NGROK}/api/computers/${computerId}/operation-mode`;
  const data = await apiClient({
    method: "PUT",
    url,
    data: { mode, email },
  });

  return data;
};

export const deviceConnectUserName = async ({
  userId,
  username,
  role,
  deviceId,
  accessPort,
}: {
  userId: number;
  username: string;
  role: string;
  deviceId: string;
  accessPort: string;
}) => {
  const url = `${NGROK}/api/connection-auth`;

  const data = await apiClient({
    method: "POST",
    url,
    data: {
      userId,
      username,
      role,
      computerId: deviceId,
      accessPort,
    },
  });

  return data;
};

export const changeTagsForDevice = async ({
  deviceId,
  existingTags,
  newTags,
  tenantName
}: {
  deviceId: string;
  existingTags: Tag[];
  newTags: Tag[];
  tenantName: string;
}) => {
  const url = `${NGROK}/api/computers/tags/configure/${deviceId}?tenantName=${tenantName}`;

  const data = await apiClient({
    method: "POST",
    url,
    data: {
      existingTags: existingTags.map(tag => tag.name),
      newTags: newTags.map(tag => tag.name),
    },
  });

  return data;
};

export const getAllDeviceAccessInfo = async (email: string, page: number, limit: number, searchMachine: string, tenantName: string) => {
  let url = `${NGROK}/api/${tenantName}/devices/access-info?email=${email}&page=${page + 1}&limit=${limit}`;

  if (searchMachine.length > 0) {
    url += `&searchparams=${searchMachine}`;
  }

  const data = await apiClient({
    method: "GET",
    url
  });
  return data;
};

export const updateTheMachine = async (computerId: string) => {
  let url = `${NGROK}/api/agent/update`;
  let payload = {
    computer_id: computerId,
  }
  const data = await apiClient({
    method: "POST",
    url,
    data: payload
  });
  return data;

}

export const getUsers = async (tenantName: string, computerId: string, role: string, email: string) => {
  if (role === "TENANT_ADMIN" || role === "SYS_ADMIN") {


    const response = await apiClient<{ content: any[] }>({
      method: "GET",
      url: `${NGROK}/api/${tenantName}/computer-users/${computerId}`,
    });

    return response.content;
  } else if (role === "TENANT_USER") {
    const response = await apiClient({
      method: "GET",
      url: `${NGROK}/api/${tenantName}/computer-users/${computerId}/users?email=${email}`,
    });

    return response;
  }
};

export const addManualDevice = async (payload: AddManualDevicePayload) => {
  let url = `${NGROK}/api/add-manual-device`;

  const defaultProtocolAuth: "Key" | "Password" = payload.protocol === "SSH" ? "Key" : "Password";

  const rawCredentials: AddManualDeviceCredential[] =
    payload.credentials && payload.credentials.length > 0
      ? payload.credentials
      : [
        {
          username: payload.username,
          protocolAuth:
            payload.protocolAuth === "Key" || payload.protocolAuth === "Password"
              ? payload.protocolAuth
              : defaultProtocolAuth,
          privateKey: payload.privateKey,
          passPharse: payload.passPharse,
          password: payload.password,
          confirmPassword: payload.confirmPassword,
        },
      ];

  const normalizedCredentials: AddManualDeviceCredential[] = rawCredentials.map((credential) => ({
    ...credential,
    username: credential.username.trim(),
  }));

  const usernameTracker = new Set<string>();
  for (const credential of normalizedCredentials) {
    if (!credential.username) {
      throw new Error("Credential username is required.");
    }

    const normalizedUsername = credential.username.toLowerCase();
    if (usernameTracker.has(normalizedUsername)) {
      throw new Error("Credential username must be unique.");
    }
    usernameTracker.add(normalizedUsername);
  }

  const primaryCredential = normalizedCredentials[0];

  const requestPayload: AddManualDevicePayload = {
    ...payload,
    username: primaryCredential.username,
    password: primaryCredential.password,
    confirmPassword: primaryCredential.confirmPassword,
    protocolAuth: primaryCredential.protocolAuth,
    privateKey: primaryCredential.privateKey,
    passPharse: primaryCredential.passPharse,
    credentials: normalizedCredentials,
  };

  const data = await apiClient({
    method: "POST",
    url,
    data: requestPayload
  });
  return data;

}

export const getComputersPerDevice = async ({
  tenantName,
  email,
  role,
  userId,
  id
}: GetComputersParamsPerDevice): Promise<any> => {


  if (!email || !role || !tenantName || !id) return [];


  let url = `${NGROK}/api/${tenantName}/computers/${id}`;
  const params: Record<string, any> = {
    email,
    role,
  };

  const data = await apiClient<any[]>({
    method: "GET",
    url,
    params,
  });

  return data;
};
